import { Button } from '@/components/ui/button';
import { EasyIcon, ExhaustiveIcon, InnovationIcon, PremiumIcon } from '@/components/ui/icons';
import { MOTION } from '@/utils';
import { withBasePath } from '@/utils/basePath';
import {
  EXHAUSTIVE_ITEMS,
  INNOVATION_ITEMS,
  SERVICE_ITEMS,
  SIMPLICITY_ITEMS,
} from '@/utils/data/advantages';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import Image from 'next/image';
import { forwardRef, useCallback, useRef, type ReactNode } from 'react';

const cardClassName =
  'relative h-full flex flex-col gap-4 rounded-[var(--radius-current)] bg-white p-8 md:p-12 [box-shadow:var(--shadow-current)]';

type AdvantageCardProps = {
  title: string;
  icon: ReactNode;
  children: ReactNode;
  decoration?: ReactNode;
};

const AdvantageCard = forwardRef<HTMLDivElement, AdvantageCardProps>(
  ({ title, icon, children, decoration }, ref) => {
    return (
      <article ref={ref} className={cardClassName}>
        {decoration}

        <div className="flex items-center justify-between gap-4">
          <h3 className="font-roboto-medium text-orange! text-lg uppercase">{title}</h3>
          {icon}
        </div>

        <div className="text-dark-blue flex flex-col gap-2">{children}</div>
      </article>
    );
  },
);

AdvantageCard.displayName = 'AdvantageCard';

function ListItem({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2">
      <span aria-hidden="true" className="text-orange">
        •
      </span>
      <p>{children}</p>
    </div>
  );
}

export default function Advantage() {
  const refs = {
    section: useRef(null),
    title: useRef(null),
    intro: useRef(null),
    button: useRef(null),
    cards: useRef<HTMLDivElement[]>([]),
  };

  const setCardRef = useCallback(
    (index: number) => (el: HTMLDivElement | null) => {
      if (el) refs.cards.current[index] = el;
    },
    [],
  );

  useGSAP(
    () => {
      if (!refs.title.current || !refs.intro.current) return;

      const splitTitle = new SplitText(refs.title.current, {
        type: 'lines',
        mask: 'lines',
      });
      const splitIntro = new SplitText(refs.intro.current, {
        type: 'lines',
        mask: 'lines',
      });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: refs.section.current,
          start: 'top 60%',
          toggleActions: 'play none none reverse',
        },
      });

      tl.from(splitTitle.lines, {
        yPercent: MOTION.yPercent,
        stagger: MOTION.staggerLines,
        duration: MOTION.durationMd,
        ease: MOTION.ease,
      }).from(
        splitIntro.lines,
        {
          yPercent: MOTION.yPercent,
          stagger: MOTION.staggerLines,
          duration: MOTION.durationMd,
          ease: MOTION.ease,
        },
        '<+=0.2',
      );

      refs.cards.current.forEach((card) => {
        if (!card) return;
        tl.from(
          card,
          {
            autoAlpha: 0,
            y: MOTION.y,
            duration: MOTION.durationMd,
            ease: MOTION.ease,
          },
          '<+=0.2',
        );
      });

      if (refs.button.current) {
        tl.from(
          refs.button.current,
          {
            autoAlpha: 0,
            y: MOTION.y,
            duration: MOTION.durationMd,
            ease: MOTION.ease,
          },
          '<+=0.2',
        );
      }
    },
    { scope: refs.section },
  );

  return (
    <section
      ref={refs.section}
      className="max-width-blue-section mx-auto flex flex-col items-center gap-12 px-6 py-16 md:px-8"
      id="solutions"
    >
      <div className="flex w-full flex-col items-start gap-2">
        <h2 ref={refs.title}>Les avantages de zLawyer</h2>
        <p ref={refs.intro} aria-hidden="true" className="max-w-4xl">
          Le logiciel de gestion pensé par et pour les cabinets d&apos;avocats. La puissance de
          zLawyer repose sur un équilibre unique entre simplicité d&apos;utilisation, richesse
          fonctionnelle et qualité de service.
        </p>

        <p className="sr-only">
          Le logiciel de gestion pensé par et pour les cabinets d&apos;avocats. La puissance de
          zLawyer repose sur un équilibre unique entre simplicité d&apos;utilisation, richesse
          fonctionnelle et qualité de service.
        </p>
      </div>

      <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 md:px-8">
        <AdvantageCard ref={setCardRef(0)} icon={<ExhaustiveIcon />} title="Exhaustivité">
          <p>
            <span className="font-bold">Tout votre cabinet, enfin centralisé.</span>
          </p>
          <p>
            zLawyer regroupe l&apos;ensemble des fonctionnalités essentielles à la gestion de votre
            cabinet, au sein d&apos;un outil unique, conçu pour les avocats.
          </p>

          <div className="grid w-full grid-cols-1 justify-items-start gap-2 py-2 sm:grid-cols-2">
            {EXHAUSTIVE_ITEMS.map((item) => (
              <ListItem key={item}>
                {item === 'Et + encore...' ? (
                  <>
                    Et <span className="text-orange font-bold">+</span> encore...
                  </>
                ) : (
                  item
                )}
              </ListItem>
            ))}
          </div>
          <p>
            Une plateforme complète pour{' '}
            <span className="font-bold">
              gérer, organiser et piloter votre activité au quotidien.
            </span>
          </p>
        </AdvantageCard>

        <AdvantageCard
          ref={setCardRef(1)}
          icon={<EasyIcon />}
          title="Simplicité"
          decoration={
            <Image
              alt="panda"
              className="absolute -top-[53px] hidden w-36 md:block"
              height={144}
              src={withBasePath('/images/pandas/pandaTop.png')}
              width={144}
            />
          }
        >
          <p>
            <span className="font-bold">Un logiciel puissant, sans complexité.</span>
          </p>
          <div className="grid w-full grid-cols-1 justify-items-start gap-2 py-2 sm:grid-cols-2">
            {SIMPLICITY_ITEMS.map((item) => (
              <ListItem key={item}>
                {item === 'Et + encore...' ? (
                  <>
                    Et <span className="text-orange font-bold">+</span> encore...
                  </>
                ) : (
                  item
                )}
              </ListItem>
            ))}
          </div>
        </AdvantageCard>

        <AdvantageCard ref={setCardRef(2)} icon={<PremiumIcon />} title="Service Premium">
          <p>
            <span className="font-bold">Un accompagnement à la hauteur de vos exigences.</span>
          </p>
          <p>
            Chez zLawyer, le service ne se limite pas à un support : c&apos;est un véritable
            engagement.
          </p>
          <div className="grid w-full grid-cols-1 justify-items-start gap-2 py-2 sm:grid-cols-2">
            {SERVICE_ITEMS.map((item) => (
              <ListItem key={item}>{item}</ListItem>
            ))}
          </div>
          <p>Un partenaire fiable, impliqué à vos côtés au quotidien.</p>
        </AdvantageCard>

        <AdvantageCard
          ref={setCardRef(3)}
          icon={<InnovationIcon />}
          title="Évolutif & Innovant"
          decoration={
            <Image
              alt="panda"
              className="absolute -left-[88px] hidden h-40 w-auto md:block"
              height={160}
              src={withBasePath('/images/pandas/pandaSide.png')}
              width={120}
            />
          }
        >
          <p>
            <span className="font-bold">Un logiciel qui progresse avec vous.</span>
          </p>
          <p>zLawyer évolue en continu grâce à une démarche active de R&amp;D.</p>
          <div className="grid w-full grid-cols-1 justify-items-start gap-2 py-2 sm:grid-cols-2">
            {INNOVATION_ITEMS.map((item) => (
              <ListItem key={item}>{item}</ListItem>
            ))}
          </div>
          <p>
            Un logiciel toujours à jour, conçu pour{' '}
            <span className="font-bold">
              accompagner durablement la performance de votre cabinet.
            </span>
          </p>
        </AdvantageCard>
      </div>

      <Button ref={refs.button} className="w-fit text-center" href="/contact">
        Demandez une démo
      </Button>
    </section>
  );
}
